import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminSection } from "./admin";

export const Route = createFileRoute("/_authenticated/admin/launch")({
  head: () => ({
    meta: [
      { title: "Launch Readiness · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminLaunch,
});

type Level = "pass" | "warn" | "fail" | "loading";

interface Check {
  key: string;
  label: string;
  detail: string;
  level: Level;
  link?: { to: string; label: string };
}

function tone(level: Level) {
  switch (level) {
    case "pass":
      return { chip: "PASS", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" };
    case "warn":
      return { chip: "WARN", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" };
    case "fail":
      return { chip: "FAIL", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" };
    default:
      return { chip: "…", cls: "bg-muted/20 text-muted-foreground border-muted/30" };
  }
}

function AdminLaunch() {
  const [checks, setChecks] = useState<Check[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const results: Check[] = [];

      const [releases, songs, activationsPublished, activationsScheduled, wisdomPublished, books, shopActive, subsActive, admins] =
        await Promise.all([
          supabase.from("releases").select("*", { count: "exact", head: true }),
          supabase.from("releases").select("*", { count: "exact", head: true }).not("audio_url", "is", null),
          supabase.from("activations").select("*", { count: "exact", head: true }).eq("status", "published"),
          supabase.from("activations").select("*", { count: "exact", head: true }).eq("status", "scheduled"),
          supabase.from("wisdom_posts").select("*", { count: "exact", head: true }).eq("published", true),
          supabase.from("books").select("*", { count: "exact", head: true }),
          supabase.from("shop_products").select("*", { count: "exact", head: true }).eq("active", true),
          supabase.from("subscriptions").select("*", { count: "exact", head: true }).in("status", ["active", "trialing"]),
          supabase.from("user_roles").select("*", { count: "exact", head: true }).eq("role", "admin"),
        ]);

      const publishKey = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined) ?? "";
      const isLive = publishKey.startsWith("pk_live_");
      const isTest = publishKey.startsWith("pk_test_");

      results.push({
        key: "stripe",
        label: "Payments mode",
        detail: isLive
          ? "Stripe live keys detected. Test-mode banner auto-hides."
          : isTest
            ? "Stripe TEST keys still active. Switch to live before launch."
            : "No Stripe publishable key detected on the client bundle.",
        level: isLive ? "pass" : isTest ? "warn" : "fail",
      });

      results.push({
        key: "admins",
        label: "Admin role assigned",
        detail: `${admins.count ?? 0} account(s) hold the admin role.`,
        level: (admins.count ?? 0) >= 1 ? "pass" : "fail",
        link: { to: "/admin/customers", label: "Manage" },
      });

      results.push({
        key: "releases",
        label: "Music releases",
        detail: `${releases.count ?? 0} release(s), ${songs.count ?? 0} with audio wired.`,
        level: (releases.count ?? 0) > 0 && (songs.count ?? 0) > 0 ? "pass" : "warn",
        link: { to: "/admin/releases", label: "Open" },
      });

      const activePub = activationsPublished.count ?? 0;
      const activeSch = activationsScheduled.count ?? 0;
      results.push({
        key: "activations",
        label: "Daily activations",
        detail: `${activePub} published · ${activeSch} scheduled.`,
        level: activePub + activeSch >= 7 ? "pass" : activePub + activeSch > 0 ? "warn" : "fail",
        link: { to: "/admin/activations", label: "Open" },
      });

      results.push({
        key: "wisdom",
        label: "Wisdom archive",
        detail: `${wisdomPublished.count ?? 0} published post(s).`,
        level: (wisdomPublished.count ?? 0) >= 3 ? "pass" : (wisdomPublished.count ?? 0) > 0 ? "warn" : "fail",
        link: { to: "/admin/wisdom", label: "Open" },
      });

      results.push({
        key: "books",
        label: "Books",
        detail: `${books.count ?? 0} book(s) in catalog.`,
        level: (books.count ?? 0) > 0 ? "pass" : "warn",
        link: { to: "/admin/books", label: "Open" },
      });

      results.push({
        key: "shop",
        label: "Shop products",
        detail: `${shopActive.count ?? 0} active product(s).`,
        level: (shopActive.count ?? 0) > 0 ? "pass" : "warn",
        link: { to: "/admin/shop", label: "Open" },
      });

      results.push({
        key: "members",
        label: "Membership",
        detail: `${subsActive.count ?? 0} active or trialing subscription(s).`,
        level: "pass",
        link: { to: "/admin/subscriptions", label: "Open" },
      });

      if (!alive) return;
      setChecks(results);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const summary = checks.reduce(
    (acc, c) => {
      acc[c.level] = (acc[c.level] ?? 0) + 1;
      return acc;
    },
    {} as Record<Level, number>,
  );

  return (
    <AdminSection
      title="Launch readiness"
      description="Pass / warn / fail across content, commerce, and access. Refreshes on every visit."
    >
      <div className="mb-6 flex flex-wrap gap-2 text-xs uppercase tracking-[0.2em]">
        <span className="rounded-full border hairline px-3 py-1 text-muted-foreground">
          {loading ? "Running…" : `${checks.length} checks`}
        </span>
        {(["pass", "warn", "fail"] as const).map((l) => (
          <span key={l} className={`rounded-full border px-3 py-1 ${tone(l).cls}`}>
            {tone(l).chip} · {summary[l] ?? 0}
          </span>
        ))}
      </div>

      <ul className="space-y-3">
        {(loading
          ? (Array.from({ length: 6 }).map((_, i) => ({
              key: `s${i}`,
              label: "Loading…",
              detail: "",
              level: "loading" as Level,
            })) as Check[])
          : checks
        ).map((c) => {
          const t = tone(c.level);
          return (
            <li key={c.key} className="flex flex-wrap items-start justify-between gap-4 rounded-lg border hairline p-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-[0.2em] ${t.cls}`}>
                    {t.chip}
                  </span>
                  <p className="font-serif text-lg">{c.label}</p>
                </div>
                {c.detail && <p className="mt-2 text-sm text-muted-foreground">{c.detail}</p>}
              </div>
              {c.link && (
                <Link
                  to={c.link.to}
                  className="rounded-full border hairline px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
                >
                  {c.link.label} →
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </AdminSection>
  );
}
